import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';
import { Plus, Edit2, Trash2, Calendar, MapPin, Users, X, Upload, Gift } from 'lucide-react';
import { InlineSpinner } from '../components/InlineSpinner';
import { Spinner } from '../components/Spinner';
import { useConfirm } from '../contexts/ConfirmDialogContext';
import styles from './WorkshopsPage.module.css';

interface NamedRef {
  _id: string;
  name: string;
}

interface Workshop {
  _id: string;
  name: string;
  description: string;
  date: string;
  time: string;
  endTime: string;
  numberOfSeats: number;
  availableSeats: number;
  location: string;
  address: string;
  price: number;
  imageUrl: string;
  availableKits: NamedRef[];
  bundleName: string;
  bundleDescription: string;
  bundleImage: string;
}

const initialFormState = {
  name: '',
  description: '',
  date: '',
  time: '',
  endTime: '',
  numberOfSeats: 10,
  location: '',
  address: '',
  price: 0,
  availableKitIds: [] as string[],
  bundleName: '',
  bundleDescription: '',
};

function getImageSrc(url: string) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_BASE_URL}${url}`;
}

export function WorkshopsPage() {
  const { token } = useAuth();
  const confirm = useConfirm();
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [kits, setKits] = useState<NamedRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Main workshop image state
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string>('');

  // Bundle image state
  const [bundleImageFile, setBundleImageFile] = useState<File | null>(null);
  const [existingBundleImage, setExistingBundleImage] = useState<string>('');

  const fetchWorkshops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/workshops`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setWorkshops(data.workshops || []);
    } catch (error) {
      console.error('Error fetching workshops:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchKits = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/painting-kits`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setKits((data.kits || []).map((k: any) => ({ _id: k._id, name: k.name })));
    } catch (error) {
      console.error('Error fetching painting kits:', error);
    }
  };

  useEffect(() => {
    fetchWorkshops();
    fetchKits();
  }, [token]);

  const openCreateModal = () => {
    setEditingWorkshop(null);
    setFormData(initialFormState);
    setImageFile(null);
    setExistingImageUrl('');
    setBundleImageFile(null);
    setExistingBundleImage('');
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (workshop: Workshop) => {
    setEditingWorkshop(workshop);
    setFormData({
      name: workshop.name,
      description: workshop.description,
      date: workshop.date.split('T')[0],
      time: workshop.time,
      endTime: workshop.endTime || '',
      numberOfSeats: workshop.numberOfSeats,
      location: workshop.location,
      address: workshop.address || '',
      price: workshop.price,
      availableKitIds: (workshop.availableKits || []).map((k) => k._id),
      bundleName: workshop.bundleName || '',
      bundleDescription: workshop.bundleDescription || '',
    });
    setImageFile(null);
    setExistingImageUrl(workshop.imageUrl || '');
    setBundleImageFile(null);
    setExistingBundleImage(workshop.bundleImage || '');
    setError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingWorkshop(null);
    setFormData(initialFormState);
    setImageFile(null);
    setExistingImageUrl('');
    setBundleImageFile(null);
    setExistingBundleImage('');
    setError(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'numberOfSeats' || name === 'price' ? Number(value) : value,
    }));
  };

  const toggleKit = (kitId: string) => {
    setFormData((prev) => ({
      ...prev,
      availableKitIds: prev.availableKitIds.includes(kitId)
        ? prev.availableKitIds.filter((id) => id !== kitId)
        : [...prev.availableKitIds, kitId],
    }));
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
      setExistingImageUrl('');
    }
    e.target.value = '';
  };

  const removeImage = () => {
    setImageFile(null);
    setExistingImageUrl('');
  };

  const handleBundleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setBundleImageFile(e.target.files[0]);
      setExistingBundleImage('');
    }
    e.target.value = '';
  };

  const removeBundleImage = () => {
    setBundleImageFile(null);
    setExistingBundleImage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!imageFile && !existingImageUrl) {
      setError('Please add a workshop image');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const url = editingWorkshop
        ? `${API_BASE_URL}/api/workshops/${editingWorkshop._id}`
        : `${API_BASE_URL}/api/workshops`;
      const method = editingWorkshop ? 'PUT' : 'POST';

      const fd = new FormData();
      fd.append('name', formData.name);
      fd.append('description', formData.description);
      fd.append('date', formData.date);
      fd.append('time', formData.time);
      fd.append('endTime', formData.endTime);
      fd.append('numberOfSeats', String(formData.numberOfSeats));
      fd.append('location', formData.location);
      fd.append('address', formData.address);
      fd.append('price', String(formData.price));
      fd.append('availableKits', JSON.stringify(formData.availableKitIds));
      fd.append('bundleName', formData.bundleName);
      fd.append('bundleDescription', formData.bundleDescription);

      if (imageFile) {
        fd.append('image', imageFile);
      } else if (editingWorkshop) {
        fd.append('existingImageUrl', existingImageUrl);
      }

      if (bundleImageFile) {
        fd.append('bundleImage', bundleImageFile);
      } else if (editingWorkshop) {
        fd.append('existingBundleImage', existingBundleImage);
      }

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: fd,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save workshop');
      }

      closeModal();
      fetchWorkshops();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm({
      message: 'Are you sure you want to delete this workshop? This cannot be undone.',
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;

    setDeletingId(id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/workshops/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to delete workshop');
      }

      await fetchWorkshops();
    } catch (err: any) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Workshops</h1>
          <p>Manage your painting workshops</p>
        </div>
        <button className={styles.addBtn} onClick={openCreateModal}>
          <Plus size={20} />
          Add Workshop
        </button>
      </header>

      {loading ? (
        <Spinner label="Loading workshops..." size="lg" />
      ) : workshops.length === 0 ? (
        <div className={styles.empty}>
          <p>No workshops yet. Create your first workshop!</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {workshops.map((workshop) => (
            <div key={workshop._id} className={styles.card}>
              <div className={styles.cardImage}>
                <img src={getImageSrc(workshop.imageUrl)} alt={workshop.name} />
              </div>
              <div className={styles.cardContent}>
                <h3>{workshop.name}</h3>
                <p className={styles.description}>{workshop.description}</p>
                <div className={styles.details}>
                  <span>
                    <Calendar size={16} />
                    {formatDate(workshop.date)} at {workshop.time}
                    {workshop.endTime ? ` - ${workshop.endTime}` : ''}
                  </span>
                  <span>
                    <MapPin size={16} />
                    {workshop.address || workshop.location}
                  </span>
                  <span>
                    <Users size={16} />
                    {workshop.availableSeats}/{workshop.numberOfSeats} seats
                  </span>
                  {workshop.availableKits && workshop.availableKits.length > 0 && (
                    <span>
                      Kit options: {workshop.availableKits.map((k) => k.name).join(', ')}
                    </span>
                  )}
                  {workshop.bundleName && (
                    <span className={styles.bundleTag}>
                      <Gift size={16} />
                      {workshop.bundleName}
                    </span>
                  )}
                </div>
                <div className={styles.cardFooter}>
                  <span className={styles.price}>EGP {workshop.price}</span>
                  <div className={styles.actions}>
                    <button
                      className={styles.editBtn}
                      onClick={() => openEditModal(workshop)}
                      disabled={deletingId === workshop._id}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDelete(workshop._id)}
                      disabled={deletingId === workshop._id}
                    >
                      {deletingId === workshop._id ? <InlineSpinner size={16} /> : <Trash2 size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeBtn} onClick={closeModal}>
              <X size={20} />
            </button>
            <h2>{editingWorkshop ? 'Edit Workshop' : 'Create Workshop'}</h2>

            {error && <div className={styles.error}>{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Workshop Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Date</label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Start Time</label>
                  <input
                    type="time"
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>End Time</label>
                  <input
                    type="time"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Number of Seats</label>
                  <input
                    type="number"
                    name="numberOfSeats"
                    value={formData.numberOfSeats}
                    onChange={handleChange}
                    min={1}
                    required
                  />
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label>Price (EGP)</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min={0}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Location (maps link)</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Google Maps link"
                    required
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label>Address (optional)</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Readable address, e.g., 12 Nile St, Zamalek, Cairo"
                />
                <p className={styles.hint}>
                  Shown to attendees instead of the maps link when filled in.
                </p>
              </div>

              <div className={styles.formGroup}>
                <label>Workshop Image</label>
                {!imageFile && !existingImageUrl ? (
                  <label className={styles.uploadArea}>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <Upload size={20} />
                    <span>Click to upload a workshop image</span>
                  </label>
                ) : (
                  <div className={styles.bundleImagePreview}>
                    <img
                      src={imageFile ? URL.createObjectURL(imageFile) : getImageSrc(existingImageUrl)}
                      alt="Workshop preview"
                    />
                    <button type="button" className={styles.removeImgBtn} onClick={removeImage}>
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Kit Options (attendees choose one when booking)</label>
                {kits.length === 0 ? (
                  <p className={styles.hint}>No paint kits yet — add some on the Paint Kits page.</p>
                ) : (
                  <div className={styles.kitChecks}>
                    {kits.map((kit) => (
                      <label key={kit._id} className={styles.checkbox}>
                        <input
                          type="checkbox"
                          checked={formData.availableKitIds.includes(kit._id)}
                          onChange={() => toggleKit(kit._id)}
                        />
                        {kit.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.formGroup}>
                <label>Bundle Name (optional)</label>
                <input
                  type="text"
                  name="bundleName"
                  value={formData.bundleName}
                  onChange={handleChange}
                  placeholder="e.g., Coffee & Pastry Bundle"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Bundle Description (optional)</label>
                <textarea
                  name="bundleDescription"
                  value={formData.bundleDescription}
                  onChange={handleChange}
                  rows={2}
                  placeholder="What's included in the bundle offered by the venue"
                />
              </div>

              <div className={styles.formGroup}>
                <label>Bundle Image (optional)</label>
                {!bundleImageFile && !existingBundleImage ? (
                  <label className={styles.uploadArea}>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleBundleImageSelect}
                      style={{ display: 'none' }}
                    />
                    <Upload size={20} />
                    <span>Click to add a bundle image</span>
                  </label>
                ) : (
                  <div className={styles.bundleImagePreview}>
                    <img
                      src={bundleImageFile ? URL.createObjectURL(bundleImageFile) : getImageSrc(existingBundleImage)}
                      alt="Bundle preview"
                    />
                    <button type="button" className={styles.removeImgBtn} onClick={removeBundleImage}>
                      <X size={14} />
                    </button>
                  </div>
                )}
              </div>

              <button type="submit" className={styles.submitBtn} disabled={submitting}>
                {submitting ? 'Saving...' : editingWorkshop ? 'Update Workshop' : 'Create Workshop'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
